import { computed, watch, type MaybeRef, unref } from 'vue';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useApi, useToaster, ClientReviewRequestSchema } from '@shopexperts/shared';
import type { ClientReviewRequestsQueryParams } from '@shopexperts/shared';
import { z } from 'zod';

type ClientReviewRequest = z.infer<typeof ClientReviewRequestSchema>;

/**
 * Composable for fetching review requests received by the authenticated client.
 * 
 * Uses the client review requests API to retrieve a paginated list of review
 * requests that experts have sent to the client.
 * 
 * @param status - Optional reactive filter for request status
 *   - 'pending': Requests awaiting review
 *   - 'completed': Requests where review was submitted
 *   - 'expired': Expired requests
 * 
 * @example
 * ```ts
 * const { reviewRequests, isLoading } = useClientReviewRequests();
 * 
 * // With status filter
 * const { reviewRequests } = useClientReviewRequests('pending');
 * ```
 */
export function useClientReviewRequests(status?: MaybeRef<ClientReviewRequestsQueryParams['status'] | undefined>) {
  const api = useApi();
  const toaster = useToaster();

  const statusValue = computed(() => unref(status));

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: computed(() => ['client', 'review-requests', statusValue.value]),
    queryFn: ({ pageParam }) => {
      const query: ClientReviewRequestsQueryParams = { limit: 20 };
      if (pageParam) query.cursor = pageParam;
      if (statusValue.value) query.status = statusValue.value;
      
      return api.client.reviewsV2.getReviewRequests({ query });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  watch(error, (newError) => {
    if (newError) {
      toaster.create({
        title: 'Failed to load review requests',
        description: newError.message || 'An error occurred while fetching review requests.',
        type: 'error',
      });
      console.error('[useClientReviewRequests] Error:', newError);
    }
  });

  const reviewRequests = computed<ClientReviewRequest[]>(() => {
    return data.value?.pages.flatMap((page) => page.data) ?? [];
  });

  return {
    reviewRequests,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  };
}

